/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import { Dispatch, Fragment, SetStateAction, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { message, Tag as TagAntd, Popover, Tooltip, Empty } from 'antd';
// local imports
import { useStyles } from '~src/hooks/useStyles';
import {
  idOrNull,
  toggleFolderNotify,
  toggleVFileNotify,
} from '~src/services/api';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useProjectId } from '~src/hooks/useProjectId';
import { sortByModified, sortByType, TableEntry } from './FileTable.utils';
import IconButton from '~components/_common/IconButton/IconButton';
import { VersionSelect } from '~components/_common/VersionSelect/VersionSelect';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { styleFn } from './FileTable.style';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import {
  DbFile,
  DbFolder,
  DbFocusOptions,
  DbTender,
  DbTenderFolder,
} from '~src/types/db';

import { SearchContext } from '~src/contexts/SearchContext';
import { queryCache, useMutation, useQueryCache } from 'react-query';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import { useSortByRevision } from '~src/hooks/utils/useSortByRevision';
import { formatDate } from '~src/utils/formatDate';
import _ from 'lodash';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { getTagsForFile } from '~src/utils/tags';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';

import FolderTenderHammer from '~src/assets/nav-icons/folder-tender-hammer.svg';

import { useTenderFolders } from '~src/hooks/api-hooks/useTenderFolders';

export interface TenderPropertiesTableProps extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<TableEntry[]>>;
  handleRow: Required<TenderPropertiesTableProps>['onRow'];
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
  parentContainerHeight?: string;
  onlyUnassignedFolders?: boolean;
}
/** Default TenderPropertiesTable, used with folder navigation */
export const TenderPropertiesTable: React.FC<TenderPropertiesTableProps> = ({
  selectedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  suMode,
  handleRow,
  setSelectedItems,
  onEmptyOverlay,
  focusOptions,
  parentContainerHeight,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const currentFolder = useCurrentFolder();
  const folderId = currentFolder?.id || null;
  const permissionLevel = currentFolder?.permission || 0;
  const projectId = useProjectId();
  const history = useHistory();
  const archiveSettings = useArchiveSettings();
  const tagsData = archiveSettings.data?.resTags;
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { fileToView, setFileToView } = useFileViewerContext();
  const filesQueryBaseKeys = {
    folderId: idOrNull(folderId),
    showDeleted: suMode,
    withVersions: true,
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
  };

  const { sortByRevision } = useSortByRevision();
  const foldersQueryBaseKeys = {
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  };

  /* const filesQuery = useFiles(filesQueryBaseKeys, {
    enabled: !!currentFolder && permissionLevel > 1,
    onError: () =>
      // go to /archive
      history.replace(`/projects/${projectId}/archive`),
  }); */
  const foldersQuery = useTenderFolders();
  // const foldersQuery = useTenders();
  const [
    doUpdateFileNotifications,
    updateFileNotificationMutation,
  ] = useMutation(toggleVFileNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { versionedFileId }) => {
      queryCache.setQueryData<DbFile[]>(
        ['files', { ...filesQueryBaseKeys, projectId: String(projectId) }],
        (prevFiles = []) => {
          const changedFileIndex = prevFiles.findIndex(
            (item) => item.id === versionedFileId
          );
          if (changedFileIndex >= 0) {
            const newList = [...prevFiles];
            newList[changedFileIndex] = {
              ...newList[changedFileIndex],
              is_notify,
            };
            return newList;
          }
          return prevFiles;
        }
      );
    },
  });

  const [
    doUpdateFolderNotifications,
    updateFolderNotificationMutation,
  ] = useMutation(toggleFolderNotify, {
    onError: () => {
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
    },
    onSuccess: ({ is_notify }, { folderId }) => {
      queryCache.setQueryData<DbFolder[]>(
        ['folders', { ...foldersQueryBaseKeys, projectId: String(projectId) }],
        (prevFolders = []) => {
          const changedFolderIndex = prevFolders.findIndex(
            (item) => item.id === folderId
          );
          if (changedFolderIndex >= 0) {
            const newList = [...prevFolders];
            newList[changedFolderIndex] = {
              ...newList[changedFolderIndex],
              is_notify,
            };
            return newList;
          }
          return prevFolders;
        }
      );
    },
  });

  const searchContext = useContext(SearchContext);

  const foldersSource = useMemo(
    () =>
      (foldersQuery.data || [])
        .filter((v) => {
          return (
            !tableProps.onlyUnassignedFolders ||
            v.options.tender_registration_id == null
          );
        })
        .map(dbTenderToTableEntry),
    [foldersQuery.data, tableProps.onlyUnassignedFolders]
  );

  const dataSource = useMemo(() => [...foldersSource], [foldersSource]);

  const filteredFolders2 = [
    ...foldersSource /* .filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          item.dbFile?.versions &&
            item.dbFile?.versions[0] &&
            item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }), */,
  ];

  const filteredFolders = useMemo(() => [...foldersSource], [foldersSource]);

  /*  const filteredFiles = [
    ...filesSource.filter((item) => {
      //getting index of version in [versions] array by checking fileVersionsContext and [all current versions] array
      //Array.prototype.findIndex() || gets the index of the first element in the array that satisfies the provided testing function
      const dynamicIndex = (item.dbFile?.versions || []).findIndex(
        (el) =>
          //provided testing function
          el.version === fileVersionsContext[item.id]?.version &&
          fileVersionsContext[item.id]?.version
      );
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(
          dynamicIndex > 0
            ? item.dbFile?.versions &&
                item.dbFile?.versions[dynamicIndex] &&
                item.dbFile?.versions[dynamicIndex].version
            : item.dbFile?.versions &&
                item.dbFile?.versions[0] &&
                item.dbFile?.versions[0].version,
          searchContext.query
        )
      ) {
        return item;
      }
      return null;
    }),
  ];
 */
  const getBackgroundRow = (record: TableEntry) => {
    const data =
      record.dbFolder && record.dbFolder.focus
        ? _.find(focusOptions, {
            value: record.dbFolder.focus,
          })
        : '';

    if (
      (record.key === highlightedRow?.key && data) ||
      record.disabled ||
      window.sessionStorage.getItem('appState') === 'timeMachine'
    )
      return {
        color: 'black',
        background: `${data ? '#' + data.color_hex_code_opacity : ''}`,
      };
    return {
      color: 'black',
      background: `${data ? '#' + data.color_hex_code : ''}`,
    };
  };

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 40,
        align: 'center',
        render: (type, record) => {
          const isTenderFolder = record.dbFolder?.option?.is_tender_folder;
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <NavIcon
                customStyle={{ width: '28%', float: 'none' }}
                icon={FolderTenderHammer}
              />
            ),
          };
        },
        sorter: sortByType,
      },
      /*   {
        title: intl.formatMessage({ id: 'action' }),
        width: 72,
        style: { background: 'red' },
        align: 'center',
        dataIndex: 'type',
        render: (type, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (type.icon === 'file-pdf' ||
              type.icon === 'file-image') && (
              <IconButton
                click={() => {
                  if (
                    record.dbFile?.versions &&
                    record.dbFile?.versions?.length > 0
                  ) {
                    setFileToView(record.dbFile);
                  } else {
                    message.error(
                      intl.formatMessage({ id: 'file-preview-error' })
                    );
                  }
                }}
                iconStyle={type.iconStyle}
                icon={['far', 'eye']}
                color="red"
              />
            ),
          };
        },
      }, */
      {
        title: intl.formatMessage({ id: 'reference' }),
        dataIndex: 'reference',
        width: 80,
        render: (_, record) => {
          const options = record.dbTenderFolder?.options;
          const refNum = options?.tender_reference;
          const tenderId = options?.tender_global_serial;

          let listDisplay = '-';
          if (refNum) {
            listDisplay = String(refNum);
          } else if (tenderId) {
            listDisplay = `<${tenderId}>`;
          }

          const hoverDisplay = (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                {intl.formatMessage({ id: 'tender-id-column' })}:{' '}
                {tenderId || '-'}
              </span>
              <span>
                {intl.formatMessage({ id: 'tender-number-column' })}:{' '}
                {refNum || '-'}
              </span>
            </div>
          );

          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Tooltip title={hoverDisplay}>
                <div
                  style={{
                    display: 'inline-block',
                    width: '100%',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  {listDisplay}
                </div>
              </Tooltip>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        //sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: (
              <Fragment>
                {record.dbTenderFolder?.options.alias_title || name}
              </Fragment>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'title' }),
        dataIndex: 'name',
        //sorter: (a, b) => a.name.localeCompare(b.name),
        width: 180,
        render: (name, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: <Fragment>{name}</Fragment>,
          };
        },
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        width: 175,
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),
        ellipsis: true,
        render: (node, record) => {
          return {
            props: {
              style: getBackgroundRow(record),
            },
            children: node,
          };
        },
      },
      // {
      //   title: intl.formatMessage({ id: 'modified-date' }),
      //
      //   dataIndex: 'updatedAt',
      //   sorter: sortByModified(fileVersionsContext),
      //
      //   width: 140,
      //   align: 'center',
      //   render: (updatedAt, record) => {
      //     const modified = Number(record?.dbTenderFolder?.updated_at);
      //
      //     return {
      //       props: {
      //         style: getBackgroundRow(record),
      //       },
      //       children: formatDate(modified, 'short'),
      //     };
      //   },
      // },
    ],
    [
      fileVersionsContext,
      intl,
      styles.icons,
      styles.small,
      focusOptions,
      highlightedRow,
      tagsData,
    ]
  );

  // disable selection (cherrypicker) if no RIGHTS_LEVEL.OPEN on current folder
  const selectionDisabled = permissionLevel < RIGHTS_LEVEL.OPEN;

  /** calculate !getCheckboxProps.disabled */
  const isRecordSelectDisabled = (record: TableEntry) => {
    if (window.sessionStorage.getItem('appState') === 'superUser') {
      return false;
    }

    // if root, use folder permission
    if (folderId === null && record.dbFolder) {
      return record.dbFolder.permission < RIGHTS_LEVEL.OPEN;
    }
    if (suMode && record.state === DB_STATE.REMOVED) {
      return false;
    }

    return selectionDisabled || record.disabled;
  };

  /*   const handleSelectShift = (record: TableEntry, index?: number) => {
    if (selectedKeys && selectedKeys.length === 0) {
      return setSelectedItems((prevSelectedItems) => [
        record,
        ...prevSelectedItems,
      ]);
    }
    if (selectedKeys && selectedKeys.length > 0) {
      const selectedIndex = selectedKeys.findIndex(
        (element) => element === record.key
      );
      if (selectedIndex >= 0) {
        return setSelectedItems((prevItems) =>
          prevItems.filter((item) => item.key !== record.key)
        );
      }
      const tableKeys = getTableData().map((item) => item.key);

      const isAnySelectedInCurrentView = selectedKeys.some((el) =>
        tableKeys.includes(el as string)
      );
      if (!isAnySelectedInCurrentView) {
        return setSelectedItems((prevSelectedItems) => [
          record,
          ...prevSelectedItems,
        ]);
      }

      const lastIndexReversed = getTableData()
        .reverse()
        .findIndex((el) => selectedKeys.includes(el.key));

      const lastIndex = getTableData().findIndex(
        (el) => el.key === getTableData().reverse()[lastIndexReversed].key
      );
      const firstIndex = getTableData().findIndex((el) =>
        selectedKeys.includes(el.key)
      );

      if ((index as number) >= 0) {
        if ((index as number) < firstIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(index, firstIndex),
          ]);
        }

        if ((index as number) > lastIndex) {
          return setSelectedItems((prevState) => [
            ...prevState,
            ...getTableData().slice(lastIndex + 1, (index as number) + 1),
          ]);
        }

        if ((index as number) > firstIndex && (index as number) < lastIndex) {
          return setSelectedItems((prevState) =>
            uniq([
              ...prevState,
              ...getTableData().slice(firstIndex, (index as number) + 1),
            ])
          );
        }
      }
    }
  }; */

  const content = (
    <Fragment>
      <Table<TableEntry>
        css={[
          styles.table,
          {
            '.ant-table-body': {
              height: 'auto !important',
              maxHeight: 'calc(38vh - 55px) !important',
            },
          },
        ]}
        scroll={{
          y: 'calc(38vh - 55px)',
          x: 'max-content',
        }}
        showSorterTooltip={false}
        className={`main-content-window ${
          window.sessionStorage.getItem('appState') === 'timeMachine' &&
          'timeMachine-table'
        } ${suMode && 'su-table'}`}
        pagination={false}
        bordered
        columns={columns}
        dataSource={dataSource}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({
                id: 'no-data-properties-panel',
              })}
            />
          ),
        }}
        loading={
          /*  filesQuery.isLoading || */ foldersQuery.isLoading ||
          foldersQuery.isIdle
        }
        rowClassName={(record) => {
          let className = suMode ? 'su-row-table ' : '';
          if (window.sessionStorage.getItem('appState') === 'timeMachine') {
            className += 'timeMachine-row-table' + ' ';
          }
          if (
            record.key === highlightedRow?.key &&
            record.dbFolder &&
            !record.dbFolder.focus
          ) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        /*     rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          renderCell: (type, record, element, node) => {
            return {
              props: {
                style: getBackgroundRow(record),
              },
              children: node,
            };
          },
          onSelect,
          onSelectAll,
          getCheckboxProps: (record) => ({
            disabled: isRecordSelectDisabled(record),
          }),
        }} */
        onRow={(record, index) => ({
          ...handleRow(record, index),
          onClick: (event) => {
            /*     if (event.shiftKey) {
              return handleSelectShift(record, index);
            } */
            const onClickAction = handleRow(record, index).onClick;
            if (onClickAction) {
              return onClickAction(event);
            }
          },
        })}
        {...tableProps}
      />
      <div
        onContextMenu={() => {
          onEmptyOverlay && onEmptyOverlay();
        }}
        className="test-class"
        style={{ height: 'inherit', width: '100%' }}></div>
    </Fragment>
  );

  return (
    <div
      style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        borderBottom: ' 1px solid #707070',
        // maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 20px)`,
        //maxHeight: '40%',
        //background: 'red',
      }}>
      {/*   <div
        style={{
          minWidth: 1150,
          // width: 1065,
          //height: '100vh',
          //maxHeight: '80%',
          display: 'flex',
          //maxHeight: tableProps.scroll && `calc(${tableProps.scroll.y} + 20px)`,
          flexDirection: 'column',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}> */}
      {content}
      {/*  </div> */}
    </div>
  );
};

export const dbTenderToTableEntry = (dbTender: DbTenderFolder): TableEntry => {
  const { id, name, description, options } = dbTender;
  const { root_folder_id } = options;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    name: name || 'Unknown',
    description,
    version: null,
    createdAt: 0,
    updatedAt: 0,
    parentId: String(root_folder_id),
    id: id,
    notifications: false,
    state: DB_STATE.AVAILABLE,
    reference: dbTender?.options?.tender_registration_id,
    disabled: false,
    favourite: false,
    isOnTenderPropertyPanel: true,
    dbTenderFolder: dbTender,
  };
};

export const dbTenderToTableEntry2 = (dbTender: DbTender): TableEntry => {
  const {
    id,
    title,
    description,
    parent_id,
    created_at,
    updated_at,
    state,
    is_favorite,
    is_notify,
  } = dbTender;

  return {
    key: String(id),
    folder: true,
    moduleFile: false,
    type: {
      icon: 'folder',
      iconStyle: 'fas',
      color: 'blue',
      is_empty: false,
    },
    name: title || 'Unknown',
    description: description || '',
    notifications: Boolean(is_notify),
    version: null,
    createdAt: created_at,
    updatedAt: updated_at,
    parentId: String(parent_id),
    id,
    state,
    disabled: state !== DB_STATE.AVAILABLE,
    dbTender,
    favourite: Boolean(is_favorite),
    isOnTenderPropertyPanel: true,
  };
};
